import styled from 'styled-components';

const cobalt = 'var(--primary-blue, #0B4598)';
const coral = 'var(--primary-main, #FB7672)';
const scrubs = 'var(--text-primary, #224858)';
const surface = 'var(--common-white-main, #fff)';
const divider = 'var(--components-input-outlined-enabledborder, #84a3b0)';
const mutedSurface = 'var(--background-info, #eff5f7)';

export const Shell = styled.section`
  width: 100%;
  max-width: 74rem;
  margin: 0 auto;
  color: ${scrubs};
  box-sizing: border-box;

  *, *::before, *::after { box-sizing: border-box; }
  button, textarea, input { font: inherit; }
  button:focus-visible, textarea:focus-visible, input:focus-visible {
    outline: 3px solid ${coral};
    outline-offset: 3px;
  }
`;

export const Header = styled.header`
  margin-bottom: 1.25rem;
  h2 {
    margin: 0 0 0.35rem;
    color: ${cobalt};
    font-size: clamp(1.5rem, 3vw, 2rem);
    line-height: 1.2;
  }
  p { margin: 0; line-height: 1.5; }
`;

export const DraftLabel = styled.label`
  display: block;
  color: ${scrubs};
  font-weight: 700;
`;

export const Draft = styled.textarea`
  display: block;
  width: 100%;
  min-height: 12rem;
  margin-top: 0.5rem;
  resize: vertical;
  border: 1px solid ${divider};
  border-radius: 0.75rem;
  padding: 0.9rem;
  color: ${scrubs};
  background: ${surface};
  line-height: 1.55;
  &:disabled { background: ${mutedSurface}; }
`;

export const ComposeMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-top: 0.55rem;
  font-size: 0.9rem;
`;

export const Advisory = styled.p`
  margin: 1rem 0;
  padding: 0.75rem 0.9rem;
  border-left: 4px solid var(--warning-main, #e3a008);
  border-radius: 0.4rem;
  background: var(--writing-assist-warning-background, #fff8df);
  line-height: 1.45;
`;

export const ErrorMessage = styled.p`
  margin: 1rem 0;
  color: var(--error-main, #8b1e21);
  font-weight: 600;
  line-height: 1.45;
`;

export const LoadingPanel = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 0.75rem;
  background: ${mutedSurface};
  color: ${cobalt};
  font-weight: 700;

  &::after {
    content: '';
    display: block;
    width: 72%;
    height: 0.55rem;
    margin-top: 0.75rem;
    border-radius: 99px;
    background: linear-gradient(
      90deg,
      var(--writing-assist-loading-edge, #cbe1ea),
      ${surface},
      var(--writing-assist-loading-edge, #cbe1ea)
    );
    background-size: 200% 100%;
    animation: writing-assist-loading 1.2s ease-in-out infinite;
  }
  @keyframes writing-assist-loading {
    from { background-position: 100% 0; }
    to { background-position: -100% 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; }
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

export const ActionButton = styled.button<{ $primary?: boolean }>`
  min-height: 2.75rem;
  min-width: 9rem;
  border: 2px solid ${props => props.$primary ? cobalt : divider};
  border-radius: 999px;
  padding: 0.65rem 1.2rem;
  color: ${props => props.$primary ? surface : cobalt};
  background: ${props => props.$primary ? cobalt : surface};
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    border-color: var(--divider, #c5d2d8);
    color: var(--text-secondary, #6b7d85);
    background: ${mutedSurface};
    cursor: not-allowed;
  }
`;

export const SkipButton = styled.button`
  min-height: 2.75rem;
  border: 0;
  padding: 0.5rem;
  color: ${cobalt};
  background: transparent;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
`;

export const CardFieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

export const VisuallyHiddenLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
`;

export const VersionCard = styled.article<{ $selected: boolean }>`
  display: flex;
  min-width: 0;
  flex-direction: column;
  border: 2px solid ${props => props.$selected ? coral : 'var(--divider, #cbe1ea)'};
  border-radius: 0.9rem;
  padding: 1rem;
  background: ${props => props.$selected ? 'var(--Pink-50, #fff9f8)' : surface};
  box-shadow: ${props => props.$selected
    ? '0 0 0 2px var(--writing-assist-selected-shadow, rgba(251, 118, 114, 0.18))'
    : '0 4px 14px var(--writing-assist-card-shadow, rgba(34, 72, 88, 0.08))'};
`;

export const RadioLabel = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.65rem;
  align-items: start;
  cursor: pointer;
  input {
    width: 1.2rem;
    height: 1.2rem;
    margin: 0.15rem 0 0;
    accent-color: ${coral};
  }
  strong, span { display: block; }
  span span {
    margin-top: 0.15rem;
    color: var(--text-secondary, #5d727b);
    font-size: 0.85rem;
    font-style: italic;
  }
`;

export const CardContent = styled.div`
  width: 100%;
  max-width: 75ch;
  min-height: 0;
  margin-top: 1rem;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  line-height: 1.5;
`;

export const VersionPreview = styled.p`
  margin: 0;

  @media (max-width: 767px) {
    &[data-mobile-preview='collapsed'] {
      display: -webkit-box;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 4;
    }
  }
`;

export const PreviewButton = styled.button`
  display: none;

  @media (max-width: 767px) {
    display: inline-flex;
    align-self: flex-start;
    margin-top: 0.65rem;
    border: 0;
    padding: 0.35rem 0;
    color: ${cobalt};
    background: transparent;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
  }
`;

export const RedactionList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.75rem 0 0;
  padding: 0;
  list-style: none;
  li {
    border-radius: 999px;
    padding: 0.3rem 0.55rem;
    background: var(--writing-assist-warning-chip-background, #fff1c7);
    color: var(--writing-assist-warning-chip-text, #6a4b00);
    font-size: 0.8rem;
    font-weight: 700;
  }
`;

export const EditButton = styled.button`
  align-self: flex-start;
  margin-top: 0.8rem;
  border: 0;
  padding: 0.35rem 0;
  color: ${cobalt};
  background: transparent;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
`;

export const VersionEditor = styled.textarea`
  width: 100%;
  min-height: 12rem;
  resize: vertical;
  border: 1px solid ${divider};
  border-radius: 0.55rem;
  padding: 0.7rem;
  color: ${scrubs};
  background: ${surface};
  line-height: 1.5;
`;

export const SaveAsBase = styled.label`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  margin-top: 0.8rem;
  font-size: 0.9rem;
  line-height: 1.4;
  input {
    width: 1.1rem;
    height: 1.1rem;
    flex: 0 0 auto;
    margin-top: 0.1rem;
    accent-color: ${coral};
  }
`;
